export const parseCapacity = (value) => {
  if (value == null || value === "") return null;
  const match = String(value)
    .replace(/,/g, "")
    .match(/(\d+(\.\d+)?)/);
  if (!match) return null;
  const parsed = Number(match[1]);
  return Number.isFinite(parsed) ? parsed : null;
};

export const sumOccasionCapacities = (occasions = [], excludeId = null) =>
  (occasions || []).reduce((sum, occasion) => {
    if (excludeId && String(occasion?._id) === String(excludeId)) return sum;
    return sum + (parseCapacity(occasion?.OccasionCapacity) || 0);
  }, 0);

export const countGuestAttendees = (guestWithAttendees = []) => {
  const guests = (guestWithAttendees || []).length;
  const attendees = (guestWithAttendees || []).reduce(
    (sum, entry) => sum + (entry.attendees?.length || 0),
    0
  );
  return { guests, attendees, total: guests + attendees };
};

export const countAllocatedForOccasion = (
  guestWithAttendees = [],
  allocations = {},
  occasionId
) => {
  const target = String(occasionId);
  let count = 0;
  (guestWithAttendees || []).forEach((entry) => {
    const guestId = entry?.guest?._id;
    if (!guestId) return;
    const mainKey = `${guestId}-null`;
    if ((allocations[mainKey] || []).map(String).includes(target)) count += 1;
    (entry.attendees || []).forEach((attendee) => {
      const key = `${guestId}-${attendee._id}`;
      if ((allocations[key] || []).map(String).includes(target)) count += 1;
    });
  });
  return count;
};

export const occasionCapacityMessage = ({
  occasionName,
  capacity,
  attempted,
}) => {
  const extra = Math.max(0, Number(attempted) - Number(capacity));
  const label = occasionName ? `“${occasionName}”` : "this occasion";
  return `Guests and attendees allocated to ${label} cannot exceed its capacity of ${capacity}. ${attempted} ${
    attempted === 1 ? "person is" : "people are"
  } selected${
    extra ? `, which is ${extra} over capacity` : ""
  }. Reduce the selection for this occasion and try again.`;
};

export const eventOccasionCapacityMessage = ({
  eventCapacity,
  combinedCapacity,
  thisCapacity,
  otherCapacity,
}) => {
  const overBy = Math.max(0, combinedCapacity - eventCapacity);
  return [
    "Cannot exceed event capacity.",
    `Event capacity: ${eventCapacity}`,
    `Other occasions: ${otherCapacity}`,
    `This occasion: ${thisCapacity}`,
    `Combined: ${combinedCapacity}${overBy ? ` (${overBy} over)` : ""}`,
    "Lower this occasion, or reduce other occasions.",
  ].join("\n");
};

export const eventOccasionCapacityNotice = (values) => {
  const overBy = Math.max(
    0,
    Number(values.combinedCapacity) - Number(values.eventCapacity)
  );
  return {
    title: "Cannot exceed event capacity",
    message: "Combined occasion capacity is over the event limit.",
    rows: [
      { label: "Event capacity", value: String(values.eventCapacity) },
      { label: "Other occasions", value: String(values.otherCapacity) },
      { label: "This occasion", value: String(values.thisCapacity) },
      {
        label: "Combined",
        value: overBy
          ? `${values.combinedCapacity} (${overBy} over)`
          : String(values.combinedCapacity),
      },
    ],
    hint: "Lower this occasion, or reduce other occasions.",
  };
};

export const eventPeopleCapacityMessage = ({
  eventCapacity,
  currentTotal,
  adding = 1,
}) => {
  const attempted = currentTotal + adding;
  return `Guests and attendees cannot exceed the event capacity of ${eventCapacity}. ${currentTotal} ${
    currentTotal === 1 ? "person is" : "people are"
  } already added, and this would bring the total to ${attempted}. Remove people or choose a higher event capacity.`;
};

export const capacityApiMessage = (error, fallback = "Capacity exceeded.") =>
  error?.response?.data?.message || fallback;

export const computeSeatLayout = ({
  seatAllocationType,
  numberOfRows,
  numberOfSeatsPerRow,
  numberOfTables,
  numberOfSeatsPerTable,
} = {}) => {
  const type = String(seatAllocationType || "").trim().toLowerCase();
  const rows = Math.max(0, Number(numberOfRows) || 0);
  const seatsPerRow = Math.max(0, Number(numberOfSeatsPerRow) || 0);
  const tables = Math.max(0, Number(numberOfTables) || 0);
  const seatsPerTable = Math.max(0, Number(numberOfSeatsPerTable) || 0);
  const rowSeats = type === "table" ? 0 : rows * seatsPerRow;
  const tableSeats = type === "row" ? 0 : tables * seatsPerTable;
  let totalSeats = 0;
  if (type === "row") totalSeats = rowSeats;
  else if (type === "table") totalSeats = tableSeats;
  else if (type === "both") totalSeats = rowSeats + tableSeats;
  return {
    type: type === "row" || type === "table" || type === "both" ? type : "",
    rowSeats,
    tableSeats,
    totalSeats,
  };
};

export const seatLayoutCapacityNotice = ({
  occasionCapacity,
  totalSeats,
  rowSeats,
  tableSeats,
  type,
}) => {
  const overBy = Math.max(0, Number(totalSeats) - Number(occasionCapacity));
  const rows = [{ label: "Occasion capacity", value: String(occasionCapacity) }];
  if (type !== "table") {
    rows.push({ label: "Row seats", value: String(rowSeats || 0) });
  }
  if (type !== "row") {
    rows.push({ label: "Table seats", value: String(tableSeats || 0) });
  }
  rows.push({
    label: "Total seats",
    value: overBy ? `${totalSeats} (${overBy} over)` : String(totalSeats),
  });
  return {
    title: "Cannot exceed occasion capacity",
    message: "Row and table seats cannot exceed this occasion’s capacity.",
    rows,
    hint: "Reduce rows, seats per row, tables, or seats per table.",
  };
};
